import { useState } from 'react';

import { api } from '../api/client';
import type { SiteRow } from '../api/types';
import {
  Badge,
  Button,
  Card,
  EvidenceImage,
  Field,
  Loading,
  Modal,
  Notice,
  Select,
  Table,
} from '../components';
import { formatDate, formatDateTime, humanise } from '../format';
import { useResource } from '../hooks';
import { useSession } from '../auth/session';

/**
 * The question a photograph was taken for, recovered from its filename.
 *
 * A fallback, not the primary path: the mobile app now sends `stepId` with every capture,
 * so `item.prompt` carries the real question for anything captured from here on. This
 * exists for what was captured before that shipped — every evidence row up to that point
 * has `submission_answer_id = null` and always will, since nothing retroactively links
 * history. The app happens to name the file after the question's field key, which is
 * enough to make an old photograph's caption meaningful rather than "Attached to the visit".
 */
function fieldKeyOf(filename: string | null): string {
  if (filename === null || filename === '') return 'Attached to the visit';
  return filename.replace(/\.[^.]+$/, '');
}

/** Just enough of a person to offer them in a picker. */
interface PersonRow {
  id: string;
  fullName: string;
}

/**
 * Submissions, and why each was decided the way it was.
 *
 * The detail view is the point of this screen. A blocked visit that cannot show which
 * check failed, on what measurement, against what threshold, is a finding nobody can
 * defend — and the field user disputing it is entitled to see exactly the same thing the
 * reviewer sees.
 */

interface SubmissionRow {
  id: string;
  status: string;
  submittedAt: string | null;
  siteCode: string;
  siteName: string;
  fieldUserName: string;
  scheduledDate: string;
  workflowName: string;
  verdictSummary: string | null;
  sampledForReview: boolean;
}

interface SubmissionDetail {
  id: string;
  status: string;
  submittedAt: string | null;
  sampledForReview: boolean;
  site: { code: string; name: string; geofenceRadiusM: number | null };
  subUnitName: string | null;
  fieldUserName: string;
  scheduledDate: string;
  workflow: { name: string; versionNumber: number };
  verdict: { verdict: string; summary: string; evaluatedAt: string; engineVersion: string } | null;
  checks: Array<{
    checkKey: string;
    outcome: string;
    message: string;
    observedValue: string | null;
    thresholdValue: string | null;
  }>;
  ruleHits: Array<{ ruleKey: string; ruleName: string; action: string; explanation: string }>;
  answers: Array<{
    fieldKey: string;
    prompt: string;
    stepType: string;
    groupName: string;
    displayValue: string;
    isNotApplicable: boolean;
  }>;
  checkpoints: Array<{
    kind: string;
    latitude: string;
    longitude: string;
    accuracyM: string | null;
    reportedDistanceM: string | null;
    computedDistanceM: string | null;
    mockLocationReported: boolean | null;
    recordedAt: string;
  }>;
  evidenceCount: number;
  evidence: Array<{
    id: string;
    prompt: string | null;
    originalFilename: string | null;
    mimeType: string;
    byteSize: number;
    capturedAt: string | null;
    latitude: string | null;
    longitude: string | null;
    hasExif: string;
  }>;
  checkInSelfieEvidenceId: string | null;
  exceptionCaseId: string | null;
}

/** The cap the server applies. Named here so the notice below can say what it means. */
const PAGE_LIMIT = 200;

export function Submissions(): JSX.Element {
  const { can, config } = useSession();
  const [status, setStatus] = useState('');
  const [siteId, setSiteId] = useState('');
  const [fieldUserId, setFieldUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  /**
   * The stores and people offered in the pickers come from the same scoped endpoints the
   * list does, so a store manager is offered their own stores and nobody else's. Building
   * these from the *rows on screen* instead would have looked identical here and quietly
   * shrunk the choices to whatever the current filter happened to return.
   */
  const sites = useResource(() => api.get<{ items: SiteRow[] }>('/sites?pageSize=500'), []);

  // A store role cannot list people, so asking would 403 and leave a picker with nothing in
  // it — a control that looks broken rather than one that was never offered.
  const people = useResource(
    () =>
      can('user.view')
        ? api.get<{ items: PersonRow[] }>('/users?pageSize=500')
        : Promise.resolve({ items: [] }),
    [],
  );

  const query = new URLSearchParams({ limit: String(PAGE_LIMIT) });
  if (status !== '') query.set('status', status);
  if (siteId !== '') query.set('siteId', siteId);
  if (fieldUserId !== '') query.set('fieldUserId', fieldUserId);
  if (from !== '') query.set('from', from);
  if (to !== '') query.set('to', to);

  const submissions = useResource(
    () => api.get<{ items: SubmissionRow[]; total: number }>(`/submissions?${query.toString()}`),
    [status, siteId, fieldUserId, from, to],
  );

  const filtered = status !== '' || siteId !== '' || fieldUserId !== '' || from !== '' || to !== '';
  const total = submissions.data?.total ?? 0;
  const shown = submissions.data?.items.length ?? 0;

  return (
    <>
      <Card title="Submissions">
        <div className="toolbar">
          <Select
            label="Verdict"
            value={status}
            onChange={setStatus}
            placeholder="All"
            options={[
              { value: 'ACCEPTED', label: 'Accepted' },
              { value: 'FLAGGED', label: 'Flagged' },
              { value: 'BLOCKED', label: 'Blocked' },
              { value: 'DRAFT', label: 'Draft' },
            ]}
          />
          <Select
            label={config.siteLabel}
            value={siteId}
            onChange={setSiteId}
            placeholder="All"
            options={(sites.data?.items ?? []).map((site) => ({
              value: site.id,
              label: `${site.code} — ${site.name}`,
            }))}
          />
          {can('user.view') && (
            <Select
              label={config.fieldUserLabel}
              value={fieldUserId}
              onChange={setFieldUserId}
              placeholder="All"
              options={(people.data?.items ?? []).map((person) => ({
                value: person.id,
                label: person.fullName,
              }))}
            />
          )}
          <Field label="From" value={from} onChange={setFrom} type="date" />
          <Field label="To" value={to} onChange={setTo} type="date" />
          {filtered && (
            <Button
              variant="secondary"
              onClick={() => {
                setStatus('');
                setSiteId('');
                setFieldUserId('');
                setFrom('');
                setTo('');
              }}
            >
              Clear
            </Button>
          )}
        </div>

        {/*
          How many matched, and whether this is all of them.
          
          The list is capped, and a capped list reads as the complete answer — "nothing from
          Meadow Park" and "nothing in the first two hundred" look identical on screen and
          mean very different things.
        */}
        {!submissions.loading && submissions.error === null && (
          <p className="muted">
            {total === 0
              ? 'Nothing matches these filters.'
              : shown < total
                ? `Showing the ${shown} most recent of ${total}. Narrow the dates to see the rest.`
                : `${total} submission${total === 1 ? '' : 's'}.`}
          </p>
        )}

        {submissions.loading ? (
          <Loading />
        ) : submissions.error !== null ? (
          <Notice kind="error">{submissions.error}</Notice>
        ) : (
          <Table
            rows={submissions.data?.items ?? []}
            keyOf={(row) => row.id}
            empty="No submissions recorded."
            onRowClick={(row) => setOpenId(row.id)}
            columns={[
              { header: 'Date', render: (row) => formatDate(row.scheduledDate) },
              { header: config.siteLabel, render: (row) => `${row.siteCode} — ${row.siteName}` },
              { header: config.fieldUserLabel, render: (row) => row.fieldUserName },
              // "Form" everywhere a person reads it. The API and the schema call these
              // workflows and will keep doing so; the panel should not make a reader learn
              // two words for one thing because of an internal name.
              { header: 'Form', render: (row) => row.workflowName },
              {
                header: 'Verdict',
                render: (row) => (
                  <div className="row">
                    <Badge tone={row.status}>{row.status}</Badge>
                    {row.sampledForReview && <Badge tone="low">Spot-check</Badge>}
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {openId !== null && <SubmissionDetailView id={openId} onClose={() => setOpenId(null)} />}
    </>
  );
}

function SubmissionDetailView({ id, onClose }: { id: string; onClose: () => void }): JSX.Element {
  const detail = useResource(() => api.get<SubmissionDetail>(`/submissions/${id}`), [id]);

  return (
    <Modal title="Submission" onClose={onClose}>
      {detail.loading ? (
        <Loading />
      ) : detail.error !== null ? (
        <Notice kind="error">{detail.error}</Notice>
      ) : detail.data === null ? null : (
        <div className="stack">
          <div>
            <div className="row">
              <Badge tone={detail.data.status}>{detail.data.status}</Badge>
              {detail.data.sampledForReview && <Badge tone="low">Random spot-check</Badge>}
            </div>
            <p className="muted">
              {detail.data.site.code} — {detail.data.site.name}
              {detail.data.subUnitName === null ? '' : ` · ${detail.data.subUnitName}`} ·{' '}
              {detail.data.fieldUserName} · {detail.data.scheduledDate}
            </p>
            <p className="muted">
              {detail.data.workflow.name} (version {detail.data.workflow.versionNumber})
            </p>
          </div>

          {detail.data.verdict !== null && (
            <Notice kind={detail.data.verdict.verdict === 'ACCEPTED' ? 'success' : 'error'}>
              {detail.data.verdict.summary}
            </Notice>
          )}

          <div>
            <h3>Checks</h3>
            <Table
              rows={detail.data.checks}
              keyOf={(row) => row.checkKey}
              empty="No checks were recorded."
              columns={[
                { header: 'Check', render: (row) => <code>{row.checkKey}</code> },
                { header: 'Outcome', render: (row) => <Badge tone={outcomeTone(row.outcome)}>{row.outcome}</Badge> },
                { header: 'Detail', render: (row) => row.message },
                {
                  header: 'Measured',
                  numeric: true,
                  // The number and the limit side by side. "Failed the location check" is
                  // an assertion; "4544 m against a 200 m limit" is evidence.
                  render: (row) =>
                    row.observedValue === null
                      ? '—'
                      : `${row.observedValue}${row.thresholdValue === null ? '' : ` / ${row.thresholdValue}`}`,
                },
              ]}
            />
          </div>

          {detail.data.ruleHits.length > 0 && (
            <div>
              <h3>Rules that fired</h3>
              <Table
                rows={detail.data.ruleHits}
                keyOf={(row) => row.ruleKey}
                columns={[
                  { header: 'Rule', render: (row) => row.ruleName },
                  { header: 'Action', render: (row) => <Badge tone={row.action}>{row.action}</Badge> },
                  { header: 'Because', render: (row) => row.explanation },
                ]}
              />
            </div>
          )}

          <div>
            <h3>Where the device was</h3>
            <Table
              rows={detail.data.checkpoints}
              keyOf={(row) => `${row.kind}-${row.recordedAt}`}
              empty="No location was recorded."
              columns={[
                { header: 'Stage', render: (row) => row.kind },
                { header: 'Coordinates', render: (row) => `${Number(row.latitude).toFixed(4)}, ${Number(row.longitude).toFixed(4)}` },
                {
                  header: 'Device said',
                  numeric: true,
                  render: (row) => (row.reportedDistanceM === null ? '—' : `${Math.round(Number(row.reportedDistanceM))} m`),
                },
                {
                  header: 'Server calculated',
                  numeric: true,
                  // Disagreement between these two is the fraud signal, so they are shown
                  // next to each other rather than one being presented as "the" distance.
                  render: (row) => (row.computedDistanceM === null ? '—' : `${Math.round(Number(row.computedDistanceM))} m`),
                },
                {
                  header: 'Mock location',
                  render: (row) =>
                    row.mockLocationReported === true ? <Badge tone="high">Reported</Badge> : '—',
                },
              ]}
            />
            {detail.data.site.geofenceRadiusM !== null && (
              <p className="muted">
                Permitted radius for this {detail.data.site.name}: {detail.data.site.geofenceRadiusM} m
              </p>
            )}
          </div>

          <div>
            <h3>Answers</h3>
            <Table
              rows={detail.data.answers}
              keyOf={(row) => row.fieldKey}
              empty="No answers were recorded."
              columns={[
                { header: 'Section', render: (row) => row.groupName },
                { header: 'Question', render: (row) => row.prompt },
                {
                  header: 'Answer',
                  render: (row) =>
                    row.isNotApplicable ? <span className="muted">Not applicable</span> : row.displayValue,
                },
              ]}
            />
          </div>

          {/*
            The photographs, not a count of them.
            
            This said "4 evidence file(s)" and stopped there, which is a strange thing for a
            product whose entire claim rests on the photograph having been taken at the place
            on the day. The selfie leads because it answers a different question from the
            rest — who was there, rather than what they found.
          */}
          <div className="section">
            <h3>Photographs</h3>

            {detail.data.checkInSelfieEvidenceId === null &&
            detail.data.evidence.length === 0 ? (
              <p className="muted">No photographs were captured on this visit.</p>
            ) : (
              <div className="evidence-grid">
                {detail.data.checkInSelfieEvidenceId !== null && (
                  <div className="evidence-item">
                    <EvidenceImage
                      evidenceId={detail.data.checkInSelfieEvidenceId}
                      alt="Check-in photograph"
                    />
                    <span className="evidence-caption">
                      <strong>At check-in</strong>
                      <br />
                      Who started this visit
                    </span>
                  </div>
                )}

                {detail.data.evidence.map((item) => (
                  <div key={item.id} className="evidence-item">
                    <EvidenceImage
                      evidenceId={item.id}
                      alt={item.prompt ?? 'Evidence photograph'}
                    />
                    <span className="evidence-caption">
                      {/*
                        `item.prompt` comes from a join through `submission_answer_id`,
                        which the mobile app now sets on every capture — see `fieldKeyOf`
                        for what a photograph taken before that shipped falls back to.
                      */}
                      <strong>{item.prompt ?? humanise(fieldKeyOf(item.originalFilename))}</strong>
                      <br />
                      {/* Where and when the camera says it was taken. The validation gate
                          has already judged this; showing it lets a reader check the
                          verdict rather than take it on trust. */}
                      {item.capturedAt === null
                        ? 'No capture time recorded'
                        : formatDateTime(item.capturedAt)}
                      {item.latitude === null
                        ? ''
                        : ` · ${Number(item.latitude).toFixed(4)}, ${Number(item.longitude).toFixed(4)}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {detail.data.exceptionCaseId !== null && (
            <p className="muted">An exception case was raised for this submission.</p>
          )}
        </div>
      )}
    </Modal>
  );
}

function outcomeTone(outcome: string): string {
  if (outcome === 'PASS') return 'accepted';
  if (outcome === 'BLOCK') return 'blocked';
  if (outcome === 'FLAG') return 'flagged';
  return 'low';
}
